import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { stripImageFileExtension } from '@/utils/imageUrlUtils';
import '@/styles/ipe/ipe-auth.css';

/**
 * 图片/视频空间重命名弹窗：带出原名（不含扩展名），确认时保留原扩展名。
 */
export default function ImageSpaceRenameModal({
  open,
  image = null,
  saving = false,
  onClose,
  onConfirm,
  variant = 'image',
}) {
  const { t } = useTranslation('workbench');
  const isVideo = variant === 'video';
  const inputRef = useRef(null);
  const rawName = String(image?.name || image?.fileName || '');
  const defaultUntitled = isVideo ? t('studioChrome.unnamedVideo') : t('studioChrome.unnamedImage');
  const displayName = stripImageFileExtension(rawName) || defaultUntitled;
  const [draft, setDraft] = useState(displayName);

  useEffect(() => {
    if (!open) return;
    setDraft(displayName);
    const timer = window.setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 30);
    return () => window.clearTimeout(timer);
  }, [open, displayName, image?.id]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !saving) onClose?.();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose, saving]);

  if (!open || !image) return null;

  const submit = () => {
    const trimmedBase = stripImageFileExtension(String(draft || '').trim()) || String(draft || '').trim();
    if (!trimmedBase || saving) return;
    const extMatch = rawName.match(
      isVideo
        ? /\.(mp4|webm|mov|m4v|mkv|avi)$/i
        : /\.(png|jpe?g|webp|gif|bmp|svg|heic|avif|tiff?)$/i,
    );
    const nextName = extMatch ? `${trimmedBase}${extMatch[0]}` : trimmedBase;
    onConfirm?.(image.id, nextName);
  };

  return createPortal(
    <div
      className="ipe-account-modal"
      role="presentation"
      onClick={() => {
        if (!saving) onClose?.();
      }}
    >
      <div
        className="ipe-account-modal__panel ipe-account-modal__panel--compact"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ipe-image-space-rename-title"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="ipe-account-modal__close"
          onClick={onClose}
          disabled={saving}
          aria-label={t('studioChrome.close')}
        >
          <X size={18} />
        </button>
        <h2 id="ipe-image-space-rename-title" className="ipe-account-modal__title">
          {t('studioChrome.rename')}
        </h2>
        <div className="ipe-account-modal__form">
          <label className="ipe-account-modal__label" htmlFor="ipe-image-space-rename-input">
            {isVideo ? t('studioChrome.videoName') : t('studioChrome.imageName')}
          </label>
          <input
            id="ipe-image-space-rename-input"
            ref={inputRef}
            type="text"
            className="ipe-account-modal__input"
            value={draft}
            disabled={saving}
            maxLength={120}
            placeholder={isVideo ? t('studioChrome.videoNamePlaceholder') : t('studioChrome.imageNamePlaceholder')}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                submit();
              }
            }}
          />
        </div>
        <div className="ipe-account-modal__actions">
          <button
            type="button"
            className="ipe-account-modal__btn ipe-account-modal__btn--ghost"
            disabled={saving}
            onClick={onClose}
          >
            {t('studioChrome.cancel')}
          </button>
          <button
            type="button"
            className="ipe-account-modal__btn ipe-account-modal__btn--primary"
            disabled={saving || !String(draft || '').trim()}
            onClick={submit}
          >
            {saving ? t('studioChrome.saving') : t('studioChrome.confirm')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
